import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { DocumentsData } from '@/features/ui/DocumentsData';

import { RMPServiceCode } from '@/shared/types/serviceCodes';
import { ConditionView, FormDatePicker, FormSelect, FormTextInput, Grid, StepControlPanel } from '@/shared/ui';

import { FormConstants } from '@/components/Forms/constants';

import { useERZDocumentTypes } from '@/modules/erz/helpers';

import { formatDate } from '@/helpers/dates';

import { rmp } from '@/api/integrations/rmp';
import { queryKeys } from '@/api/queryKeys';
import { DocumentTypeCode, FormDataType } from '@/api/types';

import api from '@/api';

import { missingPersonExtractAllowedFileTypes } from '../../lib/constants';
import { MissingPersonInfoSchema } from '../../model/types';
import { SubmitNotAvailableModal } from '../SubmitNotAvailableModal';

export interface MissingPersonInfoProps {
  onDraftDelete: () => Promise<void>;
  onDraftCreate: (
    data?: { stateForm: FormDataType },
    specificStep?: (step: number) => void,
  ) => Promise<Nullable<string>>;
}

export const MissingPersonInfo: React.FC<StepProps<MissingPersonInfoProps>> = ({
  next,
  serviceCode,
  specificStep,
  onDraftDelete,
  onDraftCreate,
  onStepDraftSave,
  currentRequestId,
}) => {
  const { control, watch } = useFormContext<MissingPersonInfoSchema>();
  const first_name = watch('missingPerson.firstName');
  const last_name = watch('missingPerson.lastName');
  const patronymic = watch('missingPerson.middleName');
  const birthday = watch('missingPerson.birthday');

  const [isSubmitNotAvailableModalOpen, setIsSubmitNotAvailableModalOpen] = useState<boolean>(false);

  const { data: personStatuses = [], isLoading: isPersonStatusesLoading } = useQuery({
    queryKey: queryKeys.rmp.personStatuses(),
    queryFn: () => rmp.dictionary.getPersonStatuses().then((r) => r.data.map((s) => ({ id: s.id, label: s.name }))),
  });

  const { data: disappearanceDocs = [], isLoading: isDisappearanceDocumentTypeLoading } = useERZDocumentTypes({
    allowedDocumentTypeCodes: [DocumentTypeCode.DISAPPEARANCE_MISSING_PERSON],
  });
  const disappearanceDocumentType = disappearanceDocs[0] ?? null;

  const handleNextStep = (): void => {
    if (first_name && last_name && birthday) {
      api.services
        .verifySubmitAvailability(serviceCode as RMPServiceCode.MISSING_PERSON_EXTRACT, {
          first_name,
          last_name,
          patronymic: patronymic ?? '',
          birthday,
        })
        .then(({ data: { canSubmit } }) => {
          if (canSubmit) {
            next();
          } else {
            setIsSubmitNotAvailableModalOpen(true);
          }
        });
    } else {
      next();
    }
  };

  return (
    <ConditionView if={!isPersonStatusesLoading && !isDisappearanceDocumentTypeLoading} else={null}>
      <SubmitNotAvailableModal
        isOpen={isSubmitNotAvailableModalOpen}
        full_name={`${last_name} ${first_name}${patronymic ? ' ' + patronymic : ''}`}
        createNewRequest={async () => {
          await onDraftDelete();
          await onDraftCreate({ stateForm: {} }, specificStep);
        }}
        onClose={() => setIsSubmitNotAvailableModalOpen(false)}
      />
      <FormBlock title="Особисті дані зниклого безвісти">
        <FormTextInput
          name="missingPerson.lastName"
          control={control}
          label="Прізвище"
          placeholder="Вкажіть прізвище"
          maxLength={FormConstants.INPUT_MAX_LENGTH_255 + 1}
          required
        />
        <FormTextInput
          name="missingPerson.firstName"
          control={control}
          label="Ім'я"
          placeholder="Вкажіть імʼя"
          maxLength={FormConstants.INPUT_MAX_LENGTH_255 + 1}
          required
        />
        <FormTextInput
          name="missingPerson.middleName"
          control={control}
          label="По батькові"
          placeholder="Вкажіть по батькові"
          maxLength={FormConstants.INPUT_MAX_LENGTH_255 + 1}
        />
        <FormDatePicker name="missingPerson.birthday" control={control} label="Дата народження" maxDateToday required />
        <FormSelect name="missingPerson.status" control={control} label="Статус" options={personStatuses} required />
        <DocumentsData
          withUploader
          documentTypeSelectHidden
          maxDocumentsCount={1}
          maxFileCount={5}
          fileRequired={false}
          request_id={currentRequestId ?? ''}
          documentTypeEntities={disappearanceDocumentType ? [disappearanceDocumentType] : []}
          name="missingPerson.disappearance_document"
          documentUploadLabel="Сповіщення від ТЦК, або документи які засвідчують факт зникнення особи або перебування в полоні"
          allowedFileTypes={missingPersonExtractAllowedFileTypes}
          onSaveDraft={onStepDraftSave}
        />
      </FormBlock>

      <StepControlPanel next={handleNextStep} />
    </ConditionView>
  );
};

export const MissingPersonInfoPreview: React.FC = () => {
  const { getValues } = useFormContext<MissingPersonInfoSchema>();
  const {
    missingPerson: { firstName, lastName, middleName, birthday, status, disappearance_document },
  } = getValues();

  return (
    <Grid
      title="Особисті дані зниклого безвісти"
      data={[
        ['Прізвище', lastName],
        ['Імʼя', firstName],
        ['По батькові', middleName],
        ['Дата народження', formatDate(birthday)],
        ['Статус', status.label],
        [
          'Сповіщення від ТЦК, або документи які засвідчують факт зникнення особи або перебування в полоні',
          disappearance_document?.[0].attachments?.fileNames
            ? Object.values(disappearance_document[0].attachments.fileNames).join(', ')
            : (disappearance_document?.[0]?.attachments?.file_name ??
              disappearance_document?.[0]?.attachments?.fileName ??
              null),
        ],
      ]}
    />
  );
};
